/**
 * postMessage origin allowlist resolution + validation (pure, testable).
 *
 * Security model (Lane Q7, audit F3):
 * - There is NO wildcard default. Ever.
 * - Development builds default to the known local parents.
 * - Production builds with VITE_POSTMESSAGE_ALLOWED_ORIGINS unset FAIL CLOSED
 *   (empty allowlist => every inbound message is rejected).
 * - An explicitly configured "*" is rejected and treated as unset (fail closed
 *   in production, dev defaults in development).
 *
 * These functions are pure (no import.meta.env access) so they can be unit
 * tested with node:test without a Vite environment.
 */

export const POSTMESSAGE_ORIGINS_ENV_VAR = "VITE_POSTMESSAGE_ALLOWED_ORIGINS";

/** Local parents allowed by default in development builds only. */
export const DEV_DEFAULT_POSTMESSAGE_ORIGINS: readonly string[] = [
  "http://localhost:3900",
  "http://localhost:3000",
];

/**
 * Resolve the effective allowlist from the raw env value.
 *
 * @param rawEnvValue - raw VITE_POSTMESSAGE_ALLOWED_ORIGINS value (may be undefined)
 * @param isDevelopment - true for dev builds (import.meta.env.DEV)
 * @param logError - error sink (console.error by default; injectable for tests)
 */
export function resolvePostMessageAllowedOrigins(
  rawEnvValue: string | undefined,
  isDevelopment: boolean,
  logError: (message: string) => void = (m) => console.error(m)
): string[] {
  let configured = (rawEnvValue ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  if (configured.includes("*")) {
    logError(
      `[postMessageOrigins] ${POSTMESSAGE_ORIGINS_ENV_VAR} contains the wildcard "*", which is not allowed. ` +
        "Treating the variable as unset. Configure an explicit comma-separated origin allowlist."
    );
    configured = [];
  }

  if (configured.length > 0) return configured;

  if (isDevelopment) return [...DEV_DEFAULT_POSTMESSAGE_ORIGINS];

  // Production with no (valid) configuration: FAIL CLOSED.
  logError(
    `[postMessageOrigins] ${POSTMESSAGE_ORIGINS_ENV_VAR} is not set in a production build. ` +
      "Failing closed: ALL postMessage origins will be rejected until the env var is configured."
  );
  return [];
}

export interface OriginValidationContext {
  isDevelopment: boolean;
  /** event.source === window.parent */
  isFromParent: boolean;
}

/**
 * Validate an inbound message origin against the allowlist.
 *
 * Rules:
 * - An EMPTY allowlist rejects EVERYTHING (fail closed), including the
 *   development null-origin allowance.
 * - A bare "*" entry never matches (defense in depth: the resolver already
 *   strips it, but a "*" smuggled into the list must not grant access).
 * - "null"/empty origins are rejected, except from the direct parent in
 *   development (file:// test harnesses).
 * - A trailing-"*" entry is a prefix match (e.g. "https://preview-*").
 */
export function isOriginAllowed(
  origin: string,
  allowedOrigins: readonly string[],
  context: OriginValidationContext
): boolean {
  // Fail closed: an empty allowlist rejects everything.
  if (allowedOrigins.length === 0) return false;

  const isNullOrigin = !origin || origin === "null";
  if (isNullOrigin) {
    // Development-only allowance for file:// parents (null origin).
    return context.isDevelopment && context.isFromParent;
  }

  return allowedOrigins.some((allowed) => {
    if (allowed === "*") return false; // never honor a bare wildcard entry
    if (allowed === origin) return true;
    // Subdomain wildcard: "https://*.eigenify.com" matches any direct/nested
    // subdomain (https://app.eigenify.com) but NOT the bare apex. Checked before the
    // trailing-"*" prefix rule because such an entry also ends with a non-"*" char.
    // (#44 eigenify.com convergence.)
    const subdomainMatch = allowed.match(/^(https?:\/\/)\*\.(.+)$/);
    if (subdomainMatch) {
      const prefix = subdomainMatch[1];
      const suffix = `.${subdomainMatch[2]}`;
      return origin.startsWith(prefix) && origin.endsWith(suffix);
    }
    // Trailing wildcard: "https://preview-*" is a prefix match.
    if (allowed.length > 1 && allowed.endsWith("*")) {
      return origin.startsWith(allowed.slice(0, -1));
    }
    return false;
  });
}
